<template>
  <v-layout child-flex>
    <v-card>
      <v-card-title>
        Create a Release
        -&gt;
        <v-btn class="ml-2" :to="{ name: 'tools-deploy-release' }">Deploy Release</v-btn>
      </v-card-title>
      <v-card-text>
        <p class="text">
          Make sure to run <strong>Upload Straker translations tool</strong> before creating the release or after and then add it as a patch <br/>
          Create a ticket called <strong>Translations for Release x.xx</strong> and attach the translation request XML file from Straker here and set the ticket status to QA.
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <v-select :items="apps" v-model="release.app" :disabled="loading.create" @input="appChange"
              label="Select an app" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" class="d-flex flex-row">
            <v-select :items="tags" v-model="release.tag" :loading="loading.tags"
              :disabled="loading.create || !release.app" label="Select a tag" />
            <v-dialog v-model="createTagDialog" @input="dialogChange()" :disabled="!release.app || loading.create"
              max-width="500px">
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" v-bind="attrs" v-on="on" class="ml-4 mt-3">
                  <v-icon>mdi-plus</v-icon>
                  Tag
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">Create tag</span>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-select :items="branches" v-model="newTag.branch" :loading="loading.branches"
                      label="Select a branch" @input="branchChange" />
                  </v-row>
                  <v-row>
                    <v-text-field v-model="newTag.tag" label="Tag"></v-text-field>
                  </v-row>
                  <v-row>
                    <v-simple-table dense class="mt-2">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>Branch</th>
                            <th>Target Environment</th>
                            <th>Tag Format</th>
                            <th>Example</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Release branch (e.g. <code>release-3.21</code>)</td>
                            <td>Production / Staging</td>
                            <td><code>vX.Y.Z</code></td>
                            <td><code>v3.21.4</code></td>
                          </tr>
                          <tr>
                            <td>Release branch (e.g. <code>release-3.22</code>)</td>
                            <td>QA / Dev</td>
                            <td><code>vX.Y-rcN</code></td>
                            <td><code>v3.22-rc1</code></td>
                          </tr>
                          <tr>
                            <td>Development branch</td>
                            <td>QA / Dev</td>
                            <td><code>vX.Y-preN</code></td>
                            <td><code>v3.22-pre1</code></td>
                          </tr>
                          <tr>
                            <td>Feature branch</td>
                            <td>Dev / Feature</td>
                            <td><code>vX.Y-featN</code></td>
                            <td><code>v3.22-feat1</code></td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="error" text @click="createTagDialog = false">Cancel</v-btn>
                  <v-btn color="primary" text @click="createTag" :loading="loading.create"
                    :disabled="!newTag.branch || !newTag.tag || loading.lastTag">Create</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions v-if="status.status">
        <v-btn class="ml-2 mr-1" color="primary" @click="createRelease()" :loading="loading.create"
          :disabled="!release.app || !release.tag || loading.tags || loading.create || status.status !== 'clear'">
          {{ createBtnText }}
        </v-btn>
        <v-btn class="ml-2 mr-1" color="primary" @click="clearRelease()" :loading="loading.create"
          :disabled="!release.app || loading.create || status.status !== 'complete'">
          Clear Release
        </v-btn>
        <v-btn class="ml-2 mr-1" color="primary" @click="killRelease()" :loading="loading.create"
          :disabled="!release.app || loading.create || status.status !== 'running'">
          Kill Release
        </v-btn>
        <v-icon class="ml-2" @click="getReleaseStatus()">mdi-refresh</v-icon>
      </v-card-actions>

      <v-card-text v-if="status.progress.length > 0">
        <v-row>
          <v-col cols="12">
            <h2>Progress</h2>
            <ul class="mt-4">
              <li v-for="(event, i) in status.progress" :key="i">{{ event }}</li>
            </ul>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-if="status.output.length > 0">
        <v-row>
          <v-col cols="12">
            <h2>Release output</h2>
            <pre>{{ status.output }}</pre>
          </v-col>
        </v-row>
      </v-card-text>

    </v-card>
  </v-layout>
</template>

<script>
import { http, apiUrl } from '@/services/http';
import { incrementString } from '@/services/utils';
import { capitalizeFirstLetter } from '../services/utils';

export default {
  name: 'ToolsCreateRelease',
  components: {},
  data: () => ({
    release: {
      app: null,
      tag: null,
    },
    createTagDialog: false,
    newTag: {
      tag: null,
      branch: null,
    },
    branches: [],
    tags: [],
    versions: [],
    apps: [],
    status: {
      tag: '',
      progress: [],
      error: '',
      output: [],
    },
    loading: {
      tags: false,
      branches: false,
      lastTag: false,
      create: false,
    },
    interval: null,
    lastTagRequest: 0,
  }),
  async created() {
    const response = await http.get(`${apiUrl}/environment/apps`);
    this.apps = response.data;
  },
  methods: {
    async appChange() {
      this.release.tag = null;
      this.release.env = null;
      this.branches = [];
      this.loading.tags = true;
      await Promise.all([
        this.getTags(),
        this.getEbVersions(),
        this.startStatusCheckTimer(),
      ]);
      this.loading.tags = false;
    },
    async dialogChange() {
      if (!this.branches.length) {
        this.loading.branches = true;
        await this.getBranches();
        this.loading.branches = false;
      }
    },
    async branchChange(branch) {
      this.lastTagRequest += 1;
      const requestId = this.lastTagRequest;
      this.newTag.tag = null;
      if (!branch) {
        this.loading.lastTag = false;
        return;
      }

      this.loading.lastTag = true;
      try {
        const params = { app: this.release.app, branch };
        const response = await http.get(`${apiUrl}/repos/tags/last`, { params });
        if (this.lastTagRequest === requestId && this.newTag.branch === branch && response.data.tag) {
          this.newTag.tag = incrementString(response.data.tag);
        }
      } catch (e) {
        const err = e.response && e.response.data ? e.response.data.error : '?';
        this.$toast.error(`Failed to get last tag - ${err}`);
      } finally {
        if (this.lastTagRequest === requestId) {
          this.loading.lastTag = false;
        }
      }
    },
    async getBranches() {
      const params = { app: this.release.app };
      const response = await http.get(`${apiUrl}/repos/branches`, { params });
      this.branches = response.data;
    },
    async getTags() {
      const params = { app: this.release.app };
      const response = await http.get(`${apiUrl}/repos/tags`, { params });
      this.tags = response.data;
    },
    async getEbVersions() {
      const app = this.release.app;
      const env = this.release.env || 'dev';
      const params = { app, env };
      if (app !== 'bg') {
        params['backend-only'] = 1;
      }
      const response = await http.get(`${apiUrl}/release/versions`, { params });
      this.versions = response.data;
    },
    async createTag() {
      try {
        if (this.tags.includes(this.newTag.tag)) {
          this.$toast.error(`Tag (${this.newTag.tag}) already exists`);
        }
        this.loading.create = true;
        const data = Object.assign({ app: this.release.app }, this.newTag)
        await http.post(`${apiUrl}/repos/tags`, data);
        this.$toast.success(`Tag ${this.newTag.tag} created`);
        await this.getTags();
        this.createTagDialog = false;
      } catch (e) {
        const err = e.response && e.response.data ? e.response.data.error : '?';
        this.$toast.error(`Failed to create tag - ${err}`);
      } finally {
        this.loading.create = false;
      }
    },
    stopStatusCheckTimer() {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null;
      }
    },
    async startStatusCheckTimer() {
      this.stopStatusCheckTimer();
      if (!this.release.app) {
        return;
      }
      await this.getReleaseStatus();
      this.interval = setInterval(async () => {
        await this.getReleaseStatus();
        if (this.status.status === 'complete' || this.status.status === 'clear') {
          this.stopStatusCheckTimer();
        }
      }, 10000);
    },
    async getReleaseStatus() {
      if (!this.release.app) {
        return;
      }
      const params = { app: this.release.app };
      const response = await http.get(`${apiUrl}/release/status`, { params });
      this.status = response.data.status;
    },
    async clearRelease() {
      const data = { app: this.release.app, action: 'clear' };
      const response = await http.post(`${apiUrl}/release/status`, data);
      this.status = response.data.status;
    },
    async killRelease() {
      try {
        await this.$dialog.confirm(`Are you sure you want to kill the release creation of ${this.status.tag}?`);
      } catch {
        return;
      }
      const data = { app: this.release.app, action: 'kill' };
      const response = await http.post(`${apiUrl}/release/status`, data);
      this.status = response.data.status;
    },
    async createRelease() {
      if (this.versions.includes(this.release.tag)) {
        try {
          await this.$dialog.confirm(`${this.release.tag} version already exists. Are you sure you want to rebuild?`);
        } catch {
          return;
        }
      }

      try {
        this.loading.create = true;
        await http.post(`${apiUrl}/release/create`, this.release);
        this.$toast.info(`Release create ${this.release.tag} started`);
        setTimeout(() => {
          this.startStatusCheckTimer();
        }, 1000);
      } catch (e) {
        let err = '?';
        if (e.response && e.response.data) {
          err = e.response.data.error;
        }
        this.$toast.error(`Failed to create release - ${err}`);
      } finally {
        this.loading.create = false;
      }
    },
  },
  computed: {
    createBtnText() {
      return this.status.status === 'clear' ? 'Create Release' : capitalizeFirstLetter(this.status.status);
    },
  },
}

</script>

<style scoped>
  .text {
    strong {
      color: red;
    }
  }
</style>
